import { useCallback, useEffect, useState } from 'react';

import moment from 'moment';
import styled from 'styled-components';

import { type Invitation, type InvitationElapsedExpiry } from '@sine/sine-core-api-types';
import {
  ButtonSelect,
  DateTimePicker,
  Grid,
  Input,
  SingleSelect,
  Text,
  colors,
  spacing,
  useStylesFor as stylesFor,
} from '@sine/sine-ui-components';

import sanitizeNumberInput from '../../../../helpers/sanitizeNumberInput';

interface InvitationDurationSelectorProps {
  onChange: (properties: {
    expiry: Invitation['expiry'];
    startDate: Invitation['startDate'];
    endDate: Invitation['endDate'];
  }) => void;
  invitation: Invitation | undefined;
  errors: {
    endDate?: Array<string>;
    startDate?: Array<string>;
    validTo?: Array<string>;
  };
}

const DurationSelectorContainer = styled.div`
  display: flex;
  align-items: center;

  & > :nth-child(2) {
    margin-left: ${spacing(1)};
    margin-right: ${spacing(2)};
  }

  & > :nth-child(3) {
    margin-top: ${spacing(2.75)};
  }

  ${stylesFor('tablet').andSmaller`
    flex-wrap: wrap;

    & > :nth-child(1) {
      max-width: 50%;
    }

    & > :nth-child(2) {
      max-width: 50%;
      margin-right: 0;
      margin-left: 21px;
    }

    & > :nth-child(3) {
      text-align: center;
      width: 100%;
    }
  `}
`;

const StyledInput = styled(Input)`
  width: 210px;
  white-space: nowrap;
  span {
    height: 0;
  }
  div div {
    margin-top: 0;
  }
`;

const StyledSelect = styled(SingleSelect)`
  min-width: 100px;
  width: 150px;

  span {
    visibility: hidden;
    height: 0;
  }
`;

const StyledDateTimePicker = styled(DateTimePicker)`
  width: 100%;
  & > :nth-child(2) > :nth-child(2) {
    ${stylesFor('tablet').andSmaller`
      min-width: 40%;
    `}
  }

  .react-datepicker__day--disabled.react-datepicker__day--selected {
    background-color: ${colors.red500} !important;
  }
`;

// TODO: organise a constants file
const validToSelectorOptions = [
  { label: 'Elapsed time', value: 'elapsed' },
  { label: 'Specific time', value: 'specific' },
];

const durationUnitOptions: Array<{ label: string; value: InvitationElapsedExpiry['settings']['unit'] }> = [
  { label: 'Hours', value: 'hours' },
  { label: 'Days', value: 'days' },
  { label: 'Weeks', value: 'weeks' },
  { label: 'Months', value: 'months' },
  { label: 'Years', value: 'years' },
];

export const InvitationDurationSelector = ({ onChange, invitation, errors }: InvitationDurationSelectorProps) => {
  const [expiryType, setExpiryType] = useState<string>(
    invitation
      ? validToSelectorOptions.filter((option) => option.value === invitation.expiry.type)[0].value ||
          validToSelectorOptions[0].value
      : validToSelectorOptions[0].value,
  );
  const [invalidDate, setInvalidDate] = useState(false);

  const [validFrom, setValidFrom] = useState(
    invitation?.startDate ? new Date(invitation.startDate) : moment().startOf('hour').add(1, 'hour').toDate(),
  );
  const [validTo, setValidTo] = useState(
    invitation?.endDate ? new Date(invitation.endDate) : moment(validFrom).add(1, 'hour').toDate(),
  );

  const [durationValue, setDurationValue] = useState<number>(
    invitation && invitation.expiry.type === 'elapsed'
      ? moment(invitation.endDate).diff(moment(invitation.startDate), invitation.expiry.settings.unit)
      : invitation && invitation.expiry.type === 'specific'
      ? moment(invitation.endDate).diff(moment(invitation.startDate), 'hours')
      : 1,
  );

  const [selectedDurationUnitOption, setSelectedDurationUnitOption] = useState<{
    label: string;
    value: InvitationElapsedExpiry['settings']['unit'];
  }>(
    invitation?.expiry && 'settings' in invitation.expiry
      ? durationUnitOptions.filter(
          ({ value }) => 'settings' in invitation.expiry && value === invitation.expiry.settings.unit,
        )[0]
      : durationUnitOptions[0],
  );

  const minValidDate = moment().subtract(5, 'year').toDate();

  const handleSpecificExpiryChange = (specificExpiryDate: Date) => {
    setDurationValue(moment(specificExpiryDate).diff(moment(validFrom), selectedDurationUnitOption.value as any));

    setValidTo(specificExpiryDate);

    onChange({
      expiry: { type: 'specific' },
      startDate: validFrom,
      endDate: specificExpiryDate,
    });
  };

  const handleElapsedExpiryChange = useCallback(() => {
    if (expiryType === 'elapsed') {
      const newValidTo = moment(validFrom)
        .add(durationValue, selectedDurationUnitOption.value as any)
        .toDate();

      setValidTo(newValidTo);

      onChange({
        expiry: {
          type: 'elapsed',
          settings: {
            value: durationValue,
            unit: selectedDurationUnitOption.value,
          },
        },
        startDate: validFrom,
        endDate: newValidTo,
      });
    }
  }, [durationValue, selectedDurationUnitOption.value, validFrom, expiryType]);

  useEffect(() => {
    switch (expiryType) {
      case 'elapsed':
        onChange({
          expiry: {
            type: 'elapsed',
            settings: {
              value: durationValue,
              unit: selectedDurationUnitOption.value,
            },
          },
          startDate: validFrom,
          endDate: validTo,
        });
        break;
      case 'specific':
        onChange({
          expiry: { type: 'specific' },
          startDate: validFrom,
          endDate: validTo,
        });
        break;
    }
  }, [expiryType]);

  useEffect(() => {
    handleElapsedExpiryChange();
  }, [durationValue, handleElapsedExpiryChange, selectedDurationUnitOption]);

  useEffect(() => {
    if (!moment(validTo).isValid()) {
      setInvalidDate(true);
    }
  }, [validTo]);

  return (
    <Grid container>
      <Grid>
        <ButtonSelect
          options={validToSelectorOptions}
          onChange={(value) => setExpiryType(`${value}`)}
          value={expiryType}
          alignment='center'
          data-st-ext='expiration-options'
          noSpaceReservation
        />
      </Grid>

      <Grid md={5} data-st-ext='valid-from-grid'>
        <StyledDateTimePicker
          minDate={!invitation ? minValidDate : undefined}
          name='validFrom'
          value={validFrom}
          onChange={(value) => setValidFrom(value!)}
          label='Valid from'
          noSpaceReservation
          data-st-ext='date-picker-valid-from'
        />
      </Grid>

      <Grid md={7} data-st-ext='valid-to-grid' alignItems='stretch'>
        {expiryType === 'elapsed' && (
          <DurationSelectorContainer>
            <StyledInput
              type='number'
              label='Expire after'
              onBlur={() => {
                if (durationValue < 1) {
                  setDurationValue(1);
                }
              }}
              onKeyDown={sanitizeNumberInput}
              onChange={(value) => value && setDurationValue(parseInt(value))}
              value={Math.ceil(durationValue).toString()}
              errors={errors.validTo?.length ? ['Expiry must be a valid date'] : undefined}
              min={1}
              noSpaceReservation
            />
            <StyledSelect
              label='&nbsp;'
              errors={errors.validTo?.length ? [errors.validTo[0]] : undefined}
              options={durationUnitOptions}
              onChange={(value) => {
                setSelectedDurationUnitOption(
                  value as { label: string; value: InvitationElapsedExpiry['settings']['unit'] },
                );
              }}
              value={selectedDurationUnitOption}
              noSpaceReservation
              disableAutosort
            />
            <Text>{moment(validTo).format('LLLL')}</Text>
          </DurationSelectorContainer>
        )}

        {expiryType === 'specific' && (
          <StyledDateTimePicker
            name='specific'
            maxDate={moment(validFrom).add(5, 'year').toDate()}
            minDate={moment().toDate()}
            value={!invalidDate ? validTo : moment().add(1, 'hour').toDate()}
            onChange={(value) => value && handleSpecificExpiryChange(value)}
            errors={errors.validTo}
            label='Valid to'
            noSpaceReservation
            data-st-ext='date-picker-valid-to'
          />
        )}
      </Grid>
    </Grid>
  );
};
